import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  SafeAreaView,
} from '../utils/webComponents';
import { useNavigation, useRoute } from '../utils/navigation';
import { Style, Add, AutoAwesome, Description, ChevronRight, ArrowBack } from '@mui/icons-material';
import { useFlashcards } from '../context/FlashcardContext';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../hooks/useTranslation';

const CreateFlashcardScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { setId } = route.params || {};
  const { addFlashcard } = useFlashcards();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const [mode, setMode] = useState(null); // null, 'manual', 'ai', 'document'
  const [front, setFront] = useState('');
  const [back, setBack] = useState('');
  const [loading, setLoading] = useState(false);

  // Add hover effect styling for mode options
  React.useEffect(() => {
    const style = document.createElement('style');
    style.id = 'create-flashcard-mode-options-style';
    style.textContent = `
      [data-mode-option="true"]:hover {
        border-color: ${theme.primary} !important;
        background-color: ${theme.background} !important;
      }
    `;
    const existing = document.getElementById('create-flashcard-mode-options-style');
    if (existing) {
      document.head.removeChild(existing);
    }
    document.head.appendChild(style);
    return () => {
      const toRemove = document.getElementById('create-flashcard-mode-options-style');
      if (toRemove) {
        document.head.removeChild(toRemove);
      }
    };
  }, [theme]);

  const handleCreateFlashcard = async () => {
    if (!front.trim() || !back.trim()) {
      return;
    }

    if (!setId) {
      return;
    }

    setLoading(true);

    try {
      addFlashcard({
        setId,
        front: front.trim(),
        back: back.trim(),
      });

      // Replace current screen with set detail screen (removes create screen from history)
      navigation.replace('SetDetail', { setId });
    } catch (error) {
      console.error('Error creating flashcard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectMode = (selectedMode) => {
    if (selectedMode === 'ai') {
      navigation.navigate('AIGenerate', { setId });
    } else if (selectedMode === 'document') {
      navigation.navigate('DocumentToFlashcards', { setId });
    } else {
      setMode('manual');
    }
  };

  // Show mode selection if no mode is selected
  if (!mode) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <ArrowBack sx={{ fontSize: 24, color: theme.text }} />
          </TouchableOpacity>
          <Text style={[styles.headerTitle, { color: theme.text }]}>{t('flashcard.createFlashcards')}</Text>
          <View style={{ width: 40 }} />
        </View>
        <ScrollView 
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
        >
          <View style={styles.contentContainer}>
          <View style={[styles.content, { backgroundColor: theme.surface, borderColor: theme.border }]}>
            <Text style={[styles.title, { color: theme.text }]}>Choose Creation Method</Text>

              <View style={styles.modeOptions}>
                <TouchableOpacity
                  style={[styles.modeOption, { backgroundColor: theme.surface, borderColor: theme.border }]}
                  onPress={() => handleSelectMode('manual')}
                  data-mode-option="true"
                >
                  <View style={[styles.modeIcon, { backgroundColor: theme.primaryLight }]}>
                    <Style sx={{ fontSize: 32, color: theme.primary }} />
                  </View>
                  <View style={styles.modeTextContainer}>
                    <Text style={[styles.modeTitle, { color: theme.text }]}>{t('flashcard.manual')}</Text>
                    <Text style={[styles.modeDescription, { color: theme.textSecondary }]}>
                      Create flashcards manually
                    </Text>
                  </View>
                  <ChevronRight sx={{ fontSize: 24, color: theme.textMuted, flexShrink: 0 }} />
                </TouchableOpacity>

                <TouchableOpacity
                  style={[styles.modeOption, { backgroundColor: theme.surface, borderColor: theme.border }]}
                  onPress={() => handleSelectMode('ai')}
                  data-mode-option="true"
                >
                  <View style={[styles.modeIcon, { backgroundColor: theme.primaryLight }]}>
                    <AutoAwesome sx={{ fontSize: 32, color: theme.primary }} />
                  </View>
                  <View style={styles.modeTextContainer}>
                    <Text style={[styles.modeTitle, { color: theme.text }]}>{t('flashcard.aiGenerate')}</Text>
                    <Text style={[styles.modeDescription, { color: theme.textSecondary }]}>
                      {t('flashcard.generateFromText')}
                    </Text>
                  </View>
                  <ChevronRight sx={{ fontSize: 24, color: theme.textMuted, flexShrink: 0 }} />
                </TouchableOpacity>

                <TouchableOpacity
                  style={[styles.modeOption, { backgroundColor: theme.surface, borderColor: theme.border }]}
                  onPress={() => handleSelectMode('document')}
                  data-mode-option="true"
                >
                  <View style={[styles.modeIcon, { backgroundColor: theme.primaryLight }]}>
                    <Description sx={{ fontSize: 32, color: theme.primary }} />
                  </View>
                  <View style={styles.modeTextContainer}>
                    <Text style={[styles.modeTitle, { color: theme.text }]}>{t('set.fromDocument')}</Text>
                    <Text style={[styles.modeDescription, { color: theme.textSecondary }]}>
                      {t('flashcard.uploadDocument')}
                    </Text>
                  </View>
                  <ChevronRight sx={{ fontSize: 24, color: theme.textMuted, flexShrink: 0 }} />
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </ScrollView>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <ArrowBack sx={{ fontSize: 24, color: theme.text }} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: theme.text }]}>{t('flashcard.createFlashcard')}</Text>
        <View style={{ width: 40 }} />
      </View>
      <ScrollView 
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
      >
        {/* Main Content */}
        <View style={styles.contentContainer}>
          <View style={[styles.content, { backgroundColor: theme.surface, borderColor: theme.border }]}>
            <View style={styles.iconContainer}>
              <View style={[styles.iconCircle, { backgroundColor: theme.primaryLight }]}>
                <Style sx={{ fontSize: 48, color: theme.primary }} />
              </View>
            </View>
            
            <Text style={[styles.title, { color: theme.text }]}>{t('flashcard.createFlashcard')}</Text>

            <View style={styles.form}>
              <View style={styles.inputContainer}>
                <Text style={[styles.inputLabel, { color: theme.text }]}>{t('flashcard.frontQuestion')}</Text>
                <TextInput
                  style={[styles.textInput, { backgroundColor: theme.inputBackground || theme.surface, color: theme.text, borderColor: theme.border }]}
                  value={front}
                  onChangeText={setFront}
                  placeholder={t('flashcard.frontPlaceholder')}
                  placeholderTextColor={theme.textSecondary}
                  multiline
                  numberOfLines={3}
                />
              </View>

              <View style={styles.inputContainer}>
                <Text style={[styles.inputLabel, { color: theme.text }]}>{t('flashcard.backAnswer')}</Text>
                <TextInput
                  style={[styles.textInput, styles.textArea, { backgroundColor: theme.inputBackground || theme.surface, color: theme.text, borderColor: theme.border }]}
                  value={back}
                  onChangeText={setBack}
                  placeholder={t('flashcard.backPlaceholder')}
                  placeholderTextColor={theme.textSecondary}
                  multiline
                  numberOfLines={4}
                />
              </View>
            </View>

            <View style={styles.buttonContainer}>
              <TouchableOpacity
                style={[styles.button, styles.cancelButton, { backgroundColor: theme.surface, borderColor: theme.border }]}
                onPress={() => navigation.goBack()}
              >
                <Text style={[styles.cancelButtonText, { color: theme.text }]}>{t('common.cancel')}</Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={[styles.button, styles.createButton, { backgroundColor: theme.primary }, (loading || !front.trim() || !back.trim()) && styles.disabledButton]}
                onPress={handleCreateFlashcard}
                disabled={loading || !front.trim() || !back.trim()}
              >
                <Add sx={{ fontSize: 20, color: 'white' }} />
                <Text style={[styles.createButtonText, { color: 'white' }]}>
                  {loading ? t('flashcard.creating') : t('flashcard.createFlashcard')}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    paddingHorizontal: 24,
    paddingTop: 20,
    paddingBottom: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '700',
    flex: 1,
    textAlign: 'center',
  },
  scrollView: {
    flex: 1,
    display: 'flex',
  },
  scrollContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
    minHeight: '100vh',
  },
  contentContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    maxWidth: 600,
  },
  content: {
    width: '100%',
    maxWidth: 600,
    padding: 32,
    borderRadius: 16,
    borderWidth: 1,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    margin: '0 auto',
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 32,
  },
  iconCircle: {
    width: 80,
    height: 80,
    borderRadius: 40,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    fontSize: 24,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 32,
  },
  subtitle: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 32,
    lineHeight: 1.5,
    color: 'inherit',
  },
  modeOptions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    width: '100%',
  },
  modeOption: {
    borderRadius: 12,
    padding: 24,
    borderWidth: 1,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    minHeight: 100,
    width: '100%',
  },
  modeIcon: {
    width: 56,
    height: 56,
    borderRadius: 28,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
  },
  modeTextContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  modeTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 4,
    display: 'block',
  },
  modeDescription: {
    fontSize: 14,
    lineHeight: 1.4,
    display: 'block',
  },
  form: {
    marginBottom: 32,
  },
  inputContainer: {
    marginBottom: 32,
  },
  inputLabel: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 20,
    lineHeight: 1.8,
  },
  textInput: {
    borderRadius: 12,
    padding: 16,
    fontSize: 16,
    borderWidth: 1,
    width: '100%',
    transition: 'border-color 0.2s',
    lineHeight: 1.8,
  },
  textArea: {
    minHeight: 100,
    resize: 'vertical',
    lineHeight: 1.8,
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: 12,
  },
  button: {
    flex: 1,
    borderRadius: 12,
    padding: 16,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.2s, opacity 0.2s',
    cursor: 'pointer',
  },
  cancelButton: {
    borderWidth: 1,
  },
  createButton: {
    gap: 8,
    borderWidth: 0,
  },
  disabledButton: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  createButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
});

export default CreateFlashcardScreen;
